import { TextSprite } from '../TextSprite';
import { Viewer } from '../viewer/viewer';
import * as THREE from 'three';
import { Utils } from '../utils';

export class LabelText extends TextSprite {
  viewer: Viewer;
  offset = false;
  private _position: THREE.Vector3; // The actual position of the text without offset
  private _boundUpdateListener: () => void;

  constructor(viewer: Viewer, text = '') {
    super(text);
    this.viewer = viewer;
    this.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
    this.setBackgroundColor({ r: 0, g: 0, b: 0, a: 0.3 });
    this.setFontsize(16);
    this.setVisible(true);
    this.offset = false;
    this._position = new THREE.Vector3();

    // Store bound listener so we can remove it later
    this._boundUpdateListener = this.updateLabelScaleAndOffset.bind(this);
    viewer.addEventListener('update', this._boundUpdateListener);
  }

  // When setting the position of the label directly,use getPosition() and setPosition() over position to avoid errors with offset
  getPosition(): THREE.Vector3 {
    return this._position;
  }

  setPosition(value: THREE.Vector3) {
    this.position.copy(value);
    this._position.copy(value);
  }

  updateLabelScaleAndOffset() {
    if (!this.viewer?.scene || !this.parent) return;

    const hasParent = !(this.parent instanceof THREE.Scene); // calculations differ if position is relative to parent object instead of scene

    const camera = this.viewer.scene.getActiveCamera();
    const renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
    const clientWidth = renderAreaSize.width;
    const clientHeight = renderAreaSize.height;

    const referencePos = hasParent ? this.parent.getWorldPosition(new THREE.Vector3()) : this._position;

    const distance = camera.position.distanceTo(referencePos);
    const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

    const scale = 70 / pr;
    this.scale.set(scale, scale, scale);

    if (this.offset && this.visible) {
      const labelOffset = scale * 0.5;
      // Offset in screen-up direction so position is independent of camera angle
      const screenUp = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
      if (hasParent) {
        screenUp.transformDirection(new THREE.Matrix4().copy(this.parent.matrixWorld).invert());
      }
      this.position.copy(this._position);
      this.position.addScaledVector(screenUp, labelOffset);
      // NOTE: Do NOT call this.update() here - it re-creates the texture every frame
      // which is extremely expensive. Position changes don't need texture updates.
    }
  }

  dispose() {
    this.viewer.removeEventListener('update', this._boundUpdateListener);
    super.dispose();
  }
}
